import type {
  ConfirmRecapAction,
  ConfirmRoundRecapAction,
  DrawAndPlaceAction,
  HorrorRaceState,
  SelectCharactersAction,
} from "@cloudnest/horror-race-engine";

export type HorrorRaceReplayEvent =
  | {
      readonly eventType: "init";
      readonly payload: { readonly state: HorrorRaceState };
    }
  | {
      readonly eventType: "select_characters";
      readonly payload: Omit<SelectCharactersAction, "type">;
    }
  | {
      readonly eventType: "request_draw";
      readonly payload?: { readonly playerId?: string };
    }
  | {
      readonly eventType: "shuffle_pool";
      readonly payload: { readonly seed: number };
    }
  | {
      readonly eventType: "draw_coins";
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "place_coins";
      readonly payload: Omit<DrawAndPlaceAction, "type">;
    }
  | {
      readonly eventType: "forfeit_turn";
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "resolve_round";
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "confirm_round_recap";
      readonly payload: Omit<ConfirmRoundRecapAction, "type">;
    }
  | {
      readonly eventType: "advance_round";
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "confirm_recap";
      readonly payload: Omit<ConfirmRecapAction, "type">;
    }
  | {
      readonly eventType: "score_race";
      readonly payload?: Readonly<Record<string, never>>;
    };

export type HorrorRaceReplayEventType = HorrorRaceReplayEvent["eventType"];

export interface HorrorRaceEventInput {
  readonly eventType: string;
  readonly payload?: Record<string, unknown>;
}

export const HORROR_RACE_REPLAY_EVENT_TYPES = [
  "init",
  "select_characters",
  "request_draw",
  "shuffle_pool",
  "draw_coins",
  "place_coins",
  "forfeit_turn",
  "resolve_round",
  "confirm_round_recap",
  "advance_round",
  "confirm_recap",
  "score_race",
] as const satisfies readonly HorrorRaceReplayEventType[];

const HORROR_RACE_HOST_ONLY_EVENT_TYPES: ReadonlySet<HorrorRaceReplayEventType> =
  new Set([
    "init",
    "shuffle_pool",
    "draw_coins",
    "resolve_round",
    "advance_round",
    "score_race",
  ]);

export function isHorrorRaceHostOnlyReplayEvent(
  eventType: HorrorRaceReplayEventType,
): boolean {
  return HORROR_RACE_HOST_ONLY_EVENT_TYPES.has(eventType);
}

export function normalizeHorrorRaceReplayEventType(
  eventType: string,
): HorrorRaceReplayEventType {
  switch (eventType) {
    case "init":
    case "select_characters":
    case "request_draw":
    case "shuffle_pool":
    case "draw_coins":
    case "place_coins":
    case "forfeit_turn":
    case "resolve_round":
    case "confirm_round_recap":
    case "advance_round":
    case "confirm_recap":
    case "score_race":
      return eventType;
    default:
      throw new Error(`Unsupported Horror Race event type: ${eventType}`);
  }
}

export function toHorrorRaceReplayEvent(
  event: HorrorRaceEventInput,
): HorrorRaceReplayEvent {
  const eventType = normalizeHorrorRaceReplayEventType(event.eventType);
  const payload = event.payload ?? {};

  switch (eventType) {
    case "init":
      return {
        eventType,
        payload: payload as { readonly state: HorrorRaceState },
      };
    case "select_characters":
      return {
        eventType,
        payload: payload as Omit<SelectCharactersAction, "type">,
      };
    case "request_draw":
      return {
        eventType,
        payload: payload as { readonly playerId?: string },
      };
    case "shuffle_pool":
      return {
        eventType,
        payload: payload as { readonly seed: number },
      };
    case "draw_coins":
    case "forfeit_turn":
    case "resolve_round":
    case "advance_round":
    case "score_race":
      return { eventType };
    case "place_coins":
      return {
        eventType,
        payload: payload as Omit<DrawAndPlaceAction, "type">,
      };
    case "confirm_round_recap":
      return {
        eventType,
        payload: payload as Omit<ConfirmRoundRecapAction, "type">,
      };
    case "confirm_recap":
      return {
        eventType,
        payload: payload as Omit<ConfirmRecapAction, "type">,
      };
  }
}

export function toHorrorRaceReplayEvents(
  events: readonly HorrorRaceEventInput[],
): readonly HorrorRaceReplayEvent[] {
  return events.map(toHorrorRaceReplayEvent);
}
